import {
  ANNOUNCEMENT_DISPLAY_FREQUENCIES,
  ANNOUNCEMENT_MESSAGE_TYPES,
  type AnnouncementDisplayFrequency,
  type AnnouncementMessageType,
  type IActiveAnnouncement,
  type IAnnouncement,
  type IAnnouncementContent,
  type IAnnouncementData,
  type IAnnouncementWire,
  type IUserAnnouncementWire,
} from '../types/announcements'

/**
 * Translation between the announcement model the UI is written against and the
 * one EVGPT-1725 serves.
 *
 * The server keeps an opaque `data` object plus four scalars it can filter on
 * (`is_active`, `published_at`, `repeat`, `display_duration`). Everything the
 * UI needs beyond those scalars lives in `data`, and the scalars are *mirrored*
 * from it on write — so the server's own filtering still means something, while
 * `data` stays the single source of truth on read.
 *
 * One domain concept has no wire representation at all: scheduling. There is no
 * `starts_at` column, so `scheduled_start_at` rides in `data` and the two ends
 * of the window are reconciled here — see `publishWindowHours`.
 */

const HOURS_PER_DAY = 24
const SECONDS_PER_HOUR = 3600
const SECONDS_PER_DAY = 86_400

/**
 * `display_duration` is `ge=1` hours, so "no expiry" needs a sentinel rather
 * than `null`. Ten years reads as never within any window the product cares
 * about, and is far enough from a real duration to be unambiguous on read.
 */
const NO_EXPIRY_HOURS = 10 * 365 * HOURS_PER_DAY

/** Same trick for `repeat`, whose "show once, ever" has no `null` either. */
const NEVER_REPEAT_HOURS = NO_EXPIRY_HOURS

/** What a freshly created announcement starts from. */
export const ANNOUNCEMENT_CONTENT_DEFAULTS: IAnnouncementContent = {
  message_type: 'warning',
  body_html: '<h3>Headline</h3><p>Message goes here</p>',
  header_image_url: null,
  is_dismissal_remembered: true,
  button_label: null,
  display_duration_days: 3,
  // Per-session rather than `until_dismissed`: the "Don't remind me again"
  // checkbox is what sets `is_dismissed`, and it only appears when
  // `is_dismissal_remembered` is on. An admin who turns that off while leaving
  // `until_dismissed` (repeat 0, so no cooldown) ships a notice that returns on
  // every load with no way for the user to stop it — and the modal is
  // acknowledge-only, so there is no X to fall back on. Defaulting to
  // `once_per_session` bounds the worst case to one showing per tab session.
  display_frequency: 'once_per_session',
  scheduled_start_at: null,
}

/**
 * The frequency enum projected onto the two scalars the server understands.
 * Lossy in one direction — `until_dismissed` and `once_per_session` both leave
 * `repeat` at 0 — which is why `data.display_frequency` is what gets read back.
 */
const FREQUENCY_TO_WIRE: Record<
  AnnouncementDisplayFrequency,
  { repeat: number; show_on_login: boolean }
> = {
  until_dismissed: { repeat: 0, show_on_login: false },
  once: { repeat: NEVER_REPEAT_HOURS, show_on_login: false },
  every_7_days: { repeat: 7 * HOURS_PER_DAY, show_on_login: false },
  once_per_session: { repeat: 0, show_on_login: true },
}

// #region Reading `data`

const readString = (data: IAnnouncementData, key: string): string | null =>
  typeof data[key] === 'string' ? (data[key] as string) : null

const readBoolean = (data: IAnnouncementData, key: string): boolean | null =>
  typeof data[key] === 'boolean' ? (data[key] as boolean) : null

/** `null` is a meaningful value for the nullable numbers, so it round-trips. */
const readNullableNumber = (
  data: IAnnouncementData,
  key: string,
): number | null | undefined => {
  const value = data[key]
  if (value === null) return null
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

const readEnum = <T extends string>(
  data: IAnnouncementData,
  key: string,
  allowed: readonly T[],
): T | null => {
  const value = data[key]
  return typeof value === 'string' &&
    (allowed as readonly string[]).includes(value)
    ? (value as T)
    : null
}

// #endregion

/** The scalars a wire row carries alongside `data`, where they are available. */
interface WireScalars {
  repeat?: number
  display_duration?: number
  show_on_login?: boolean
}

const inferFrequency = (scalars: WireScalars): AnnouncementDisplayFrequency => {
  if (scalars.show_on_login) return 'once_per_session'
  const repeat = scalars.repeat ?? 0
  if (repeat >= NEVER_REPEAT_HOURS) return 'once'
  if (repeat > 0) return 'every_7_days'
  return 'until_dismissed'
}

const inferDurationDays = (scalars: WireScalars): number | null => {
  const hours = scalars.display_duration
  if (hours === undefined)
    return ANNOUNCEMENT_CONTENT_DEFAULTS.display_duration_days
  if (hours >= NO_EXPIRY_HOURS) return null
  return Math.round(hours / HOURS_PER_DAY)
}

/**
 * `data` → the flat content the editor and the modal read.
 *
 * Every field is optional on the way in: the server accepts any JSON object, so
 * a row could predate a field, come from a hand-written seed, or have been
 * written by a different client. `body_html` is the one field that falls back
 * to empty rather than to its default — showing a user the "Message goes here"
 * placeholder would be worse than showing them nothing, and the admin-side
 * publish blocker already rejects an empty body.
 */
export const wireToContent = (
  data: IAnnouncementData | null | undefined,
  scalars: WireScalars = {},
): IAnnouncementContent => {
  const source = data ?? {}
  const durationDays = readNullableNumber(source, 'display_duration_days')

  return {
    message_type:
      readEnum<AnnouncementMessageType>(
        source,
        'message_type',
        ANNOUNCEMENT_MESSAGE_TYPES,
      ) ?? ANNOUNCEMENT_CONTENT_DEFAULTS.message_type,
    body_html: readString(source, 'body_html') ?? '',
    header_image_url: readString(source, 'header_image_url'),
    is_dismissal_remembered:
      readBoolean(source, 'is_dismissal_remembered') ??
      ANNOUNCEMENT_CONTENT_DEFAULTS.is_dismissal_remembered,
    button_label: readString(source, 'button_label'),
    display_duration_days:
      durationDays === undefined ? inferDurationDays(scalars) : durationDays,
    display_frequency:
      readEnum<AnnouncementDisplayFrequency>(
        source,
        'display_frequency',
        ANNOUNCEMENT_DISPLAY_FREQUENCIES,
      ) ?? inferFrequency(scalars),
    scheduled_start_at:
      readNullableNumber(source, 'scheduled_start_at') ?? null,
  }
}

/**
 * The content half of a write: the whole domain object into `data`, with the
 * three scalars the server filters on mirrored out of it.
 */
export const contentToWire = (content: IAnnouncementContent) => ({
  data: { ...content } as IAnnouncementData,
  display_duration:
    content.display_duration_days === null
      ? NO_EXPIRY_HOURS
      : Math.max(1, content.display_duration_days * HOURS_PER_DAY),
  // Guarded rather than indexed straight: the frequency reaches here from a
  // radio group that casts its value, so an unrecognised one would otherwise
  // spread `undefined` and drop both scalars from the payload.
  ...(FREQUENCY_TO_WIRE[content.display_frequency] ??
    FREQUENCY_TO_WIRE.until_dismissed),
})

/**
 * How many hours the server must keep serving the announcement, counted from
 * the publish instant rather than from the start of the display window.
 *
 * The server has no `starts_at`: it expires everything at
 * `published_at + display_duration`, and it cannot see `scheduled_start_at`
 * because that lives in `data`. Mirroring the bare duration would make a
 * scheduled notice burn its window before it opened — and never be served at
 * all when the delay exceeds the duration. Sending the whole publish→expiry
 * span instead lines the server's end up with the one the admin UI shows; the
 * client still holds the *start* back (`hasAnnouncementStarted`).
 *
 * `publishedAt` is the instant the publish call is being made, so this is only
 * meaningful at that moment — which is why `contentToWire` keeps mirroring the
 * plain duration for a draft nobody is serving yet.
 */
export const publishWindowHours = (
  content: IAnnouncementContent,
  publishedAt: number,
): number => {
  if (content.display_duration_days === null) return NO_EXPIRY_HOURS
  const delaySeconds = Math.max(
    0,
    (content.scheduled_start_at ?? publishedAt) - publishedAt,
  )
  return Math.max(
    1,
    Math.ceil(delaySeconds / SECONDS_PER_HOUR) +
      content.display_duration_days * HOURS_PER_DAY,
  )
}

/**
 * The `display_duration` a *content* update has to send.
 *
 * `contentToWire` mirrors the plain duration, which is right for a draft nobody
 * is serving. For a published announcement it is wrong: it overwrites the
 * publish→expiry span `publishWindowHours` sized at publish time, so a single
 * autosave after a typo fix would shrink a scheduled notice's window back and
 * expire it before its start — leaving it never shown to anyone.
 *
 * `undefined` means "let the mirror stand".
 */
export const contentUpdateWindowHours = (
  announcement: Pick<IAnnouncement, 'status' | 'published_at'>,
  content: IAnnouncementContent,
): number | undefined =>
  announcement.status === 'published' && announcement.published_at !== null
    ? publishWindowHours(content, announcement.published_at)
    : undefined

/**
 * When the display window opens and closes.
 *
 * Both are derived here rather than read: `is_expired` counts from
 * `published_at`, which is the wrong instant for a scheduled notice. These are
 * what the admin UI shows, and `publishWindowHours` is what makes the server
 * agree with them.
 */
const resolveWindow = (
  publishedAt: number | null,
  content: IAnnouncementContent,
): { starts_at: number | null; expires_at: number | null } => {
  const startsAt = content.scheduled_start_at ?? publishedAt
  if (startsAt === null || content.display_duration_days === null) {
    return { starts_at: startsAt, expires_at: null }
  }
  return {
    starts_at: startsAt,
    expires_at: startsAt + content.display_duration_days * SECONDS_PER_DAY,
  }
}

/** An admin row, flattened into the shape the editor reads. */
export const wireToAnnouncement = (wire: IAnnouncementWire): IAnnouncement => {
  const content = wireToContent(wire.data, wire)

  return {
    ...content,
    ...resolveWindow(wire.published_at, content),
    id: String(wire.id),
    name: wire.title,
    status: wire.is_active ? 'published' : 'draft',
    published_at: wire.published_at,
    created_at: wire.created_at,
    updated_at: wire.updated_at,
  }
}

/** An entry from `GET /users/me`, flattened for the modal. */
export const wireToActiveAnnouncement = (
  wire: IUserAnnouncementWire,
): IActiveAnnouncement => ({
  ...wireToContent(wire.data, { show_on_login: wire.show_on_login }),
  id: String(wire.id),
  published_at: wire.published_at,
})

/**
 * Whether the announcement's window has opened.
 *
 * The server has no way to honour `scheduled_start_at`, so it serves a
 * scheduled announcement from the moment it is published. Holding it back is
 * therefore the client's job until the API grows a real `starts_at` column.
 */
export const hasAnnouncementStarted = (
  announcement: IActiveAnnouncement,
  now: number = Date.now(),
): boolean =>
  announcement.scheduled_start_at === null ||
  announcement.scheduled_start_at * 1000 <= now
